
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import KnowledgeArticleView from "./KnowledgeArticleView";
import KnowledgeSearch from "./KnowledgeSearch";
import KnowledgeCollectionsList from "./KnowledgeCollectionsList";
import KnowledgeBaseEmbedded from "./KnowledgeBaseEmbedded";
import KnowledgeBaseFull from "./KnowledgeBaseFull";
import { KnowledgeCollection, KnowledgeArticle } from "./types";

type KnowledgeBaseProps = {
  companyId: string;
  isEmbedded?: boolean;
  dontAutoSelectArticle?: boolean;
};

const KnowledgeBase = ({ companyId, isEmbedded = false, dontAutoSelectArticle = false }: KnowledgeBaseProps) => {
  const [collections, setCollections] = useState<KnowledgeCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        // Fetch published collections
        const { data: collectionsData, error: collectionsError } = await supabase
          .from("knowledge_collections")
          .select("*")
          .eq("company_id", companyId)
          .eq("is_published", true)
          .order("position", { ascending: true });

        if (collectionsError) throw collectionsError;

        if (!collectionsData || collectionsData.length === 0) {
          setIsLoading(false);
          return;
        }

        // Fetch published articles for each collection
        const collectionsWithArticles = await Promise.all(
          collectionsData.map(async (collection) => {
            const { data: articlesData, error: articlesError } = await supabase
              .from("knowledge_articles")
              .select("id, title, content, slug")
              .eq("collection_id", collection.id)
              .eq("is_published", true)
              .order("position", { ascending: true });

            if (articlesError) throw articlesError;

            return {
              ...collection,
              articles: articlesData || [],
            };
          })
        );

        setCollections(collectionsWithArticles);
        
        // Select first collection by default if available, but don't select an article
        if (collectionsWithArticles.length > 0) {
          setSelectedCollection(collectionsWithArticles[0].id);
          
          // In embedded mode, also select the first article for better initial experience
          // Only if dontAutoSelectArticle is false
          if (isEmbedded && !dontAutoSelectArticle && collectionsWithArticles[0].articles.length > 0) {
            setSelectedArticle(collectionsWithArticles[0].articles[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching knowledge base:", error);
        toast({
          title: "Error",
          description: "Failed to load knowledge base",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollections();
  }, [companyId, toast, isEmbedded, dontAutoSelectArticle]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      // Basic search implementation
      const { data: articlesData, error } = await supabase
        .from("knowledge_articles")
        .select("id, title, content, slug, collection_id")
        .eq("company_id", companyId)
        .eq("is_published", true)
        .ilike("title", `%${searchQuery}%`)
        .limit(10);

      if (error) throw error;

      if (articlesData && articlesData.length > 0) {
        // Find the collection this article belongs to
        const article = articlesData[0];
        setSelectedCollection(article.collection_id);
        setSelectedArticle({
          id: article.id,
          title: article.title,
          content: article.content,
          slug: article.slug,
        });
      } else {
        toast({
          title: "No results",
          description: `No articles found for "${searchQuery}"`,
        });
      }
    } catch (error) {
      console.error("Error searching knowledge base:", error);
      toast({
        title: "Error",
        description: "Failed to search knowledge base",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle article selection
  const handleArticleSelection = (article: KnowledgeArticle) => {
    setSelectedArticle(article);
  };

  if (isLoading) {
    return <KnowledgeLoading />;
  }

  if (collections.length === 0) {
    return <KnowledgeEmpty />;
  }

  return isEmbedded ? (
    <KnowledgeBaseEmbedded
      collections={collections}
      selectedArticle={selectedArticle}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      handleSearch={handleSearch}
      handleArticleSelection={handleArticleSelection}
      setSelectedArticle={setSelectedArticle}
    />
  ) : (
    <KnowledgeBaseFull
      collections={collections}
      selectedCollection={selectedCollection}
      selectedArticle={selectedArticle}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      setSelectedCollection={setSelectedCollection}
      handleSearch={handleSearch}
      handleArticleSelection={handleArticleSelection}
      setSelectedArticle={setSelectedArticle}
    />
  );
};

const KnowledgeLoading = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const KnowledgeEmpty = () => (
  <div className="text-center py-8">
    <h2 className="text-lg font-bold mb-2">Knowledge Base</h2>
    <p className="text-muted-foreground text-sm">No knowledge base articles available yet.</p>
  </div>
);

export default KnowledgeBase;
